import { ReactElement, useEffect } from "react";
import { NextPageContext } from "next";
import { useTheme } from "styled-components";
import { Page } from "types/page";
import { ILatestPostsGroup } from "models/latestPosts";
import { ISEOTrendingBlock } from "models/seoTrendingBlocks";
import { ILayoutProps } from "models/base/ILayoutProps";
import CustomizedPageLayout from "components/Layouts/PageLayouts/CustomizedPageLayout/CustomizedPageLayout";
import enhanceContext from "../private/enhanceContext";
import dynamic from "next/dynamic";
import { getSeoTrendingBlocks } from "dataLayer/apisWithCache/homeApis/seoTrendingBlocksApi";
import useTrackPageView from "GTMs/useTrackPageView";
import {
  CookiesConstants,
  MAX_POSTS_IN_LATEST_POSTS_BOX,
} from "utils/constants";
import { ILiteRouterApiResponse } from "models/ILiteRouterApiResponse";
import { EnumScreenNames } from "../enums/EnumScreenNames";
import { EnumLoggingScreenNames } from "../libs/logging/types/loggingTypes";
import { loadScreenAction } from "../libs/logging/helpers/loggingActions";
import { IHome } from "../screens/Home/_private/types/home";
import { fetchHomeData } from "../screens/Home/_private/api/homeApis";
import {parseCookies} from "utils/nookies";
import continueBrowsingQs from "../screens/Home/_private/hooks/continueBrowsingQs";

const Suspense = ({ children, fallback: string }) => children;
const ChooseCountryScreen = dynamic(
  () => import("componentScreens/ChooseCountryScreen"),
  {
    suspense: false,
  }
);

const HomePage = dynamic(() => import("../screens/Home/_private/components/index"), {
  suspense: false,
});

export interface IPageProps extends ILayoutProps {
  isChangeCountryPage: boolean;
  latestPosts: ILatestPostsGroup[];
  seoTrendingBlocks: ISEOTrendingBlock[];
  homeData: IHome;
  screenName: EnumLoggingScreenNames.HOME
}

export async function getServerSideProps(
  ctx: NextPageContext,
  routerApiResponse: ILiteRouterApiResponse
) {
  const dt = Date.now();
  // console.time("HomeProfile: getServerSideProps");
  // console.time("HomeProfile: enhanceContext");
  const context = await enhanceContext(ctx);
  console.log('HomeProfile: enhanceContext ' +  (Date.now() - dt) + 'ms');
  const { isChangeCountryPage } = context;
  const parsedCookies = parseCookies(context);
  const showSmartBanner =
    parsedCookies[CookiesConstants.HIDE_SMART_BANNER] !== undefined ||
    parsedCookies[CookiesConstants.HIDE_SMART_BANNER] === "1";
  if (isChangeCountryPage) {
    await context.initSeoData(EnumScreenNames.OTHER_COUNTRIES_SCREEN);
    return {
      props: {
        countries: context.essentialData.getLiteCountries(),
        isChangeCountryPage: true,
        seoPage: context.seoPage,
        showSmartBanner,
        screenName: EnumLoggingScreenNames.HOME,
      },
    };
  }



  const qs = continueBrowsingQs(context);

  const dt2 = Date.now();
  const [_,homeData ,seoTrendingBlocks] = await Promise.all([
      context.initSeoData(EnumScreenNames.HOME_PAGE, {
      routerApiResponse,
    }),
    fetchHomeData({
      context,
      perPage: MAX_POSTS_IN_LATEST_POSTS_BOX[context.getData("source")],
      qs,
      cityId :routerApiResponse?.cityId || null,
      isBot: context.userAgent.toLowerCase().includes("bot")
    }).then(r => {console.log("HomeProfile: fetchHomeData Took " + (Date.now() - dt2)); return r;}).catch(() => null),
    getSeoTrendingBlocks(context, routerApiResponse?.cityId).then(r => { console.log("HomeProfile: getSeoTrendingBlocks Took " + (Date.now() - dt2) ); return r; }).catch((e) => null)
  ]);
  console.log("HomeProfile: Homepage APIs " + (Date.now() - dt2) + "ms");
  console.log("HomeProfile: getServerSideProps " + (Date.now() - dt) + "ms");
  return {
    props: {
      ...context.essentialData.getBasics(),
      seoPage: context.seoPage,
      routeObjectsIds: {
        cityId: routerApiResponse?.cityId || null,
      },
      seoTrendingBlocks,
      homeData,
      showSmartBanner,
      screenName: EnumLoggingScreenNames.HOME
    },
  };
}

const Home: Page<IPageProps> = (props) => {
  const { isChangeCountryPage } = useTheme();
  useTrackPageView(EnumScreenNames.HOME_PAGE);

  useEffect(() => {
    loadScreenAction({ currentScreen: EnumLoggingScreenNames.HOME });
  }, []);

  if (isChangeCountryPage) {
    return (
      <Suspense fallback="Loading Landing...">
        <ChooseCountryScreen />
      </Suspense>
    );
  }

  return <>{!isChangeCountryPage && <HomePage {...props} />}</>;
};

export const getLayout = (page: ReactElement, layoutProps) => {
  const { isChangeCountryPage } = layoutProps;
  if (isChangeCountryPage) return page;
  return (
    <CustomizedPageLayout
      linkedAds="mobile_homepage_footer desktop_homepage_footer"
      enableH1
      enableH2
      enableH3
      enableH4
      enableH5
      supportSplash={!isChangeCountryPage}
      supportBottomNavigation
      enableF1
      enableF2
      enableF3
      enableF4
      supportBottomSticky
      adName="homepageBottom-desktop"
      enableSmartBanner={!layoutProps.showSmartBanner}
    >
      {page}
    </CustomizedPageLayout>
  );
};
Home.getLayout = (page: ReactElement, layoutProps) => {
  return getLayout(page, layoutProps);
};

export default Home;
